import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  IoIosArrowBack,
  IoIosArrowForward,
  IoIosArrowDown,
  IoIosArrowUp,
} from "react-icons/io";
import { encodeId } from "../../Helper/encdec";
import UIConfig from "../../constants/UIConfig";

/**
 * Engagement date, in the timezone the business runs on.
 *
 * getMonth/getDate/getFullYear read the calendar of whoever is looking. The
 * stored value is an instant, so for a viewer east of Eastern a call late in the
 * Eastern day lands on the following date: a call at 2:40 PM EDT is 00:10 the
 * next day in IST, and the banner showed tomorrow while the Meeting Date panel
 * and the insight header — both Eastern — showed today.
 *
 * Every other date in this app already formats through
 * `UIConfig.branding.timeZone`; this was the one place reading the browser's
 * clock instead, which is why it disagreed with everything around it.
 *
 * Intl does the conversion, so there is no arithmetic here to get DST wrong.
 */
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return new Intl.DateTimeFormat("en-US", {
      timeZone: UIConfig.branding.timeZone || "America/New_York",
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
    }).format(d);
  } catch {
    return dateStr;
  }
};

// Columns rendered in the navigator (header + rows), in display order. `type`
// drives how the column sorts: dates compare by timestamp, everything else uses
// a numeric-aware, case-insensitive string compare (so "MH2" < "MH10").
const COLUMNS = [
  { key: "memberId", label: "MH ID", type: "string" },
  { key: "memberName", label: "Member Name", type: "string" },
  { key: "callCategory", label: "Call Category", type: "string" },
  { key: "agentName", label: "Agent Name", type: "string" },
  { key: "engagementDate1", label: "Engagement Date", type: "date" },
];

// Call ids come from two independent payloads (the ops table's `callId` and the
// details payload's `CallId`), so compare them leniently — a stray case or
// whitespace difference must not make the same call look like two.
const isSameCall = (a, b) =>
  !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

const compareItems = (a, b, sortColumn, sortDirection) => {
  const col = COLUMNS.find((c) => c.key === sortColumn);
  if (!col) return 0;
  const dir = sortDirection === "asc" ? 1 : -1;
  const av = a?.[sortColumn];
  const bv = b?.[sortColumn];
  if (col.type === "date") {
    const at = new Date(av).getTime();
    const bt = new Date(bv).getTime();
    const aValid = !isNaN(at);
    const bValid = !isNaN(bt);
    if (!aValid && !bValid) return 0;
    if (!aValid) return 1;
    if (!bValid) return -1;
    return (at - bt) * dir;
  }
  return (
    String(av ?? "").localeCompare(String(bv ?? ""), undefined, {
      numeric: true,
      sensitivity: "base",
    }) * dir
  );
};

const InsightNavigator = ({
  currentCallId,
  // Display info for the currently-viewed call, supplied by the page. A
  // belt-and-suspenders fallback so the current call is present in the list even
  // before the member's full call list is available in Redux.
  currentCallInfo = null,
  disabled = false,
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const activeRowRef = useRef(null);

  // Default sort: Engagement Date, latest → oldest.
  const [sortColumn, setSortColumn] = useState("engagementDate1");
  const [sortDirection, setSortDirection] = useState("desc");

  const filteredData = useSelector(
    (state) => state.operationHome.table.filteredData
  );
  const navigatorContext = useSelector(
    (state) => state.operationHome.table.navigatorContext
  );
  // The details page's member data (same source the panel uses). We fold this
  // member's calls into the navigable list so every call — including ones
  // reached via the in-panel "Meeting Date" arrows that aren't in the ops-table
  // list — has a real position and working prev/next.
  const memberDetails = useSelector(
    (state) => state.operationHome.details.data
  );

  // Use popup context list when available, otherwise fall back to main filtered data.
  const filteredList =
    navigatorContext?.length > 0 ? navigatorContext : filteredData;

  // The current member's calls (unique by CallId), shaped like the list rows so
  // they sort/render consistently.
  const memberCalls = useMemo(() => {
    const rows = memberDetails?.callDetails;
    if (!Array.isArray(rows)) return [];
    const byId = new Map();
    for (const row of rows) {
      const cid = row?.CallId;
      if (!cid || byId.has(cid)) continue;
      byId.set(cid, {
        callId: cid,
        memberId: memberDetails?.CustomerID ?? "",
        memberName: memberDetails?.FirstName ?? "",
        callCategory: row.callCategory ?? "",
        agentName: row.meetingPerson ?? "",
        engagementDate1: row.meetingDate ?? "",
      });
    }
    return Array.from(byId.values());
  }, [memberDetails]);

  // Base list + this member's calls (deduped by callId), sorted. The set is
  // stable while you're on a given member, so the total doesn't move as you page
  // through that member's calls, and every call has a distinct "x / n" slot.
  const sortedList = useMemo(() => {
    const list = Array.isArray(filteredList) ? [...filteredList] : [];
    const key = (id) => String(id).trim().toLowerCase();
    const seen = new Set(
      list.map((i) => i && i.callId).filter(Boolean).map(key)
    );
    const foldIn = (item) => {
      if (item && item.callId && !seen.has(key(item.callId))) {
        seen.add(key(item.callId));
        list.push(item);
      }
    };
    memberCalls.forEach(foldIn);
    foldIn(currentCallInfo);
    list.sort((a, b) => compareItems(a, b, sortColumn, sortDirection));
    return list;
  }, [filteredList, memberCalls, currentCallInfo, sortColumn, sortDirection]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const total = sortedList.length;

  // Current call index. It's normally present (folded in above); the `< 0`
  // branch only covers the brief render before the member data arrives, and even
  // then we show the real current call (never an unrelated row-0 call).
  const currentIndex = sortedList.findIndex((item) =>
    isSameCall(item?.callId, currentCallId)
  );
  const displayIndex = currentIndex < 0 ? 0 : currentIndex;
  // Never fall back to an unrelated row. This used to end in `sortedList[0]`,
  // which put a different call's MH ID, member, agent and date into the banner
  // while the page below showed the real one — presented as fact, with nothing
  // to signal it was a stand-in. Blanks are the honest answer when the current
  // call is not in the list yet; the page's own panels still show the truth.
  const currentItem =
    currentIndex >= 0 ? sortedList[currentIndex] : currentCallInfo || null;

  // Resolve the arrows to the nearest entry that is genuinely a *different*
  // call, rather than blindly stepping ±1. An arrow is therefore either
  // disabled or guaranteed to change the call — it can never land on the call
  // already on screen and become a silent no-op, however the list was built
  // (duplicate rows, an id that differs only in case, a stale fold-in).
  const step = (from, delta) => {
    if (from < 0) return -1;
    for (let i = from + delta; i >= 0 && i < total; i += delta) {
      const candidate = sortedList[i]?.callId;
      if (candidate && !isSameCall(candidate, currentCallId)) return i;
    }
    return -1;
  };
  const prevIndex = step(currentIndex, -1);
  const nextIndex = step(currentIndex, 1);

  const isFirst = prevIndex < 0;
  const isLast = nextIndex < 0;

  useEffect(() => {
    if (isOpen && currentIndex >= 0 && activeRowRef.current) {
      activeRowRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [isOpen, currentIndex, sortColumn, sortDirection]);

  if (total <= 1) return null;

  const handleSort = (key) => {
    if (sortColumn === key) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(key);
      // Sensible default direction per new column: dates newest-first, text A→Z.
      setSortDirection(key === "engagementDate1" ? "desc" : "asc");
    }
  };

  const navigateTo = (index) => {
    // Blocked while a PDF download is in flight (see DetailsScreen.js) — the
    // in-flight download reads the currently-displayed call live, so navigating
    // away mid-generation would silently redirect it to the wrong call.
    if (disabled) return;
    if (index < 0 || index >= sortedList.length) return;
    const item = sortedList[index];
    // Already showing it — navigating would push a duplicate history entry that
    // changes no route param, so nothing would re-render.
    if (!item?.callId || isSameCall(item.callId, currentCallId)) {
      setIsOpen(false);
      return;
    }
    const encodedMemberId = encodeId(
      String(item.memberId ?? "").replace(/\s+/g, "")
    );
    const encodedCallId = encodeId(item.callId);
    setIsOpen(false);
    // An empty segment wouldn't match the details route and would bounce the
    // user to /landing — better to do nothing than to lose the page.
    if (!encodedMemberId || !encodedCallId) return;
    navigate(`/detailsPage/${encodedMemberId}/${encodedCallId}`);
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <span className="nav-sort-icon" />;
    return (
      <span className="nav-sort-icon nav-sort-icon--active">
        {sortDirection === "asc" ? <IoIosArrowUp /> : <IoIosArrowDown />}
      </span>
    );
  };

  return (
    <div className="insight-navigator" ref={dropdownRef}>

      {/* Left Arrow */}
      <button
        className="nav-arrow-btn"
        onClick={() => navigateTo(prevIndex)}
        disabled={isFirst || disabled}
        title={disabled ? "Navigation is disabled while a PDF is generating" : "Previous record"}
      >
        <IoIosArrowBack />
      </button>

      {/* Trigger — labeled field card */}
      <div
        className={`nav-trigger${isOpen ? " nav-trigger--open" : ""}${disabled ? " icon-disabled" : ""}`}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        title={disabled ? "Navigation is disabled while a PDF is generating" : undefined}
      >
        {/* Single row — all 5 fields */}
        <div className="nav-trigger-body">
          <div className="nav-trigger-field nav-trigger-field--id">
            <span className="nav-field-label">MH ID</span>
            <span className="nav-field-value nav-field-value--id">
              {currentItem?.memberId || "—"}
            </span>
          </div>
          <div className="nav-trigger-field nav-trigger-field--name">
            <span className="nav-field-label">Member Name</span>
            <span className="nav-field-value">
              {currentItem?.memberName || "—"}
            </span>
          </div>
          <div className="nav-trigger-field nav-trigger-field--cat">
            <span className="nav-field-label">Call Category</span>
            <span className="nav-field-value">
              {currentItem?.callCategory || "—"}
            </span>
          </div>
          <div className="nav-trigger-field nav-trigger-field--agent">
            <span className="nav-field-label">Agent Name</span>
            <span className="nav-field-value">
              {currentItem?.agentName || "—"}
            </span>
          </div>
          <div className="nav-trigger-field nav-trigger-field--date">
            <span className="nav-field-label">Engagement Date</span>
            <span className="nav-field-value">
              {formatDate(currentItem?.engagementDate1)}
            </span>
          </div>
        </div>

        {/* Right: counter + chevron */}
        <div className="nav-trigger-meta">
          <span className="nav-trigger-count">
            {displayIndex + 1}&nbsp;/&nbsp;{total}
          </span>
          <span className="nav-trigger-chevron">
            {isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </span>
        </div>
      </div>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="nav-panel">

          {/* Column headers — click to sort asc/desc */}
          <div className="nav-panel-header">
            {COLUMNS.map((col) => (
              <button
                key={col.key}
                type="button"
                className={`nav-panel-th${
                  sortColumn === col.key ? " nav-panel-th--active" : ""
                }`}
                onClick={() => handleSort(col.key)}
                title={`Sort by ${col.label}`}
              >
                <span className="nav-panel-th-label">{col.label}</span>
                <SortIcon columnKey={col.key} />
              </button>
            ))}
          </div>

          <div className="nav-panel-body">
            {sortedList.map((item, idx) => {
              const isActive = idx === displayIndex && currentIndex >= 0;
              return (
                <div
                  key={item.callId || idx}
                  ref={isActive ? activeRowRef : null}
                  className={`nav-panel-row${isActive ? " nav-panel-row--active" : ""}`}
                  onClick={() => navigateTo(idx)}
                >
                  <span className="nav-cell nav-cell--id">
                    {item.memberId || "—"}
                  </span>
                  <span className="nav-cell">{item.memberName || "—"}</span>
                  <span className="nav-cell">{item.callCategory || "—"}</span>
                  <span className="nav-cell">{item.agentName || "—"}</span>
                  <span className="nav-cell">
                    {formatDate(item.engagementDate1)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Right Arrow */}
      <button
        className="nav-arrow-btn"
        onClick={() => navigateTo(nextIndex)}
        disabled={isLast || disabled}
        title={disabled ? "Navigation is disabled while a PDF is generating" : "Next record"}
      >
        <IoIosArrowForward />
      </button>

    </div>
  );
};

export default InsightNavigator;
